import { Component, Inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AddressBook } from './address-book.models';
import { AddressBookService } from './address-book.service';

export interface AddressBookNameDialogData {
  organisationId?: number;
  addressBook?: AddressBook;
}

@Component({
  selector: 'app-address-book-name-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatProgressSpinnerModule],
  template: `
    <header class="dialog-heading">
      <span><mat-icon>menu_book</mat-icon></span>
      <div>
        <h2 mat-dialog-title>{{ data.addressBook ? 'Adressbuch umbenennen' : 'Adressbuch anlegen' }}</h2>
        <p>{{ data.organisationId ? 'Für diese Organisation' : 'Für meine persönlichen Kontakte' }}</p>
      </div>
    </header>
    <mat-dialog-content>
      @if (errorMessage) { <div class="dialog-error" role="alert">{{ errorMessage }}</div> }
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Name</mat-label>
        <input matInput [formControl]="name" maxlength="255" cdkFocusInitial (keydown.enter)="submit()">
        @if (name.touched && name.hasError('required')) { <mat-error>Ein Name ist erforderlich.</mat-error> }
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="submitting" (click)="dialogRef.close()">Abbrechen</button>
      <button mat-flat-button color="primary" type="button" [disabled]="name.invalid || submitting" (click)="submit()">
        @if (submitting) { <mat-spinner diameter="18"></mat-spinner> }
        @else { <mat-icon>{{ data.addressBook ? 'save' : 'add' }}</mat-icon> }
        {{ data.addressBook ? 'Speichern' : 'Anlegen' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host { display: block; color: var(--app-surface-dark, #1e293b); }
    .dialog-heading { display: flex; gap: .85rem; padding: 1.4rem 1.5rem .35rem; }
    .dialog-heading > span { display: grid; place-items: center; width: 2.6rem; height: 2.6rem; border-radius: .75rem; color: white; background: var(--app-primary, #3454d1); }
    h2[mat-dialog-title] { margin: 0; padding: 0; font-size: 1.2rem; }
    .dialog-heading p { margin: .2rem 0 0; color: var(--app-surface-light, #64748b); }
    mat-dialog-content { display: grid; gap: .75rem; min-width: min(28rem, 75vw); padding-top: 1rem; }
    mat-form-field { width: 100%; }
    .dialog-error { padding: .7rem .8rem; border-radius: .5rem; color: #9f1239; background: #fff1f2; }
    mat-dialog-actions { padding: .75rem 1.5rem 1.25rem; }
  `]
})
export class AddressBookNameDialogComponent {
  readonly name: FormControl<string>;
  submitting = false;
  errorMessage = '';

  constructor(
    readonly dialogRef: MatDialogRef<AddressBookNameDialogComponent, AddressBook>,
    @Inject(MAT_DIALOG_DATA) readonly data: AddressBookNameDialogData,
    private readonly addressBookService: AddressBookService
  ) {
    this.name = new FormControl(data.addressBook?.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    });
  }

  submit(): void {
    this.name.markAsTouched();
    if (this.name.invalid || this.submitting) return;
    this.submitting = true;
    this.errorMessage = '';
    const request = this.data.addressBook
      ? this.addressBookService.renameAddressBook(this.data.addressBook.addressBookId, this.name.value.trim())
      : this.addressBookService.createAddressBook(this.name.value.trim(), { organisationId: this.data.organisationId });
    request.subscribe({
      next: addressBook => this.dialogRef.close(addressBook),
      error: error => {
        this.submitting = false;
        this.errorMessage = addressBookErrorMessage(error, 'Das Adressbuch konnte nicht gespeichert werden.');
      }
    });
  }
}

export function addressBookErrorMessage(error: any, fallback: string): string {
  const body = error?.error;
  if (typeof body === 'string' && body.trim()) {
    try {
      const parsed = JSON.parse(body);
      return parsed?.message ?? body;
    } catch {
      return body;
    }
  }
  if (body?.message) return body.message;
  return fallback;
}
